import * as genericService from "./genericFormServices";
import type { PLOFormFourData } from "../types/PLOs/plo_four.type";
import type { SubmissionStatus } from "../types/MainType.type";

const ENDPOINT = "plo-form-four";

export interface PLOFetchResponse {
  data: PLOFormFourData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for PLO Form Four
// ---------------------------------------------------------------------------
export const mapPloFormFourBackendToFrontend = (item: any): PLOFormFourData => {
  if (!item) return {} as PLOFormFourData;

  // Extract nested form entity from backend response structure (PLO Form Four)
  const form = item.pLOFormFour || item.ploFormFour || item.formData || item;

  return {
    id: item.id || form.id || '',
    documentNumber: item.documentNumber || form.documentNumber || '',
    formType: item.formType || form.formType || '',
    
    // Core Form Four Fields
    officeId: form.officeId || item.officeId || '',
    gender: form.gender || '',
    ageGroup: form.ageGroup || '',
    victimOrWitnessName: form.victimOrWitnessName || '',
    interviewDate: form.interviewDate || '',
    ptiFirstDate: form.ptiFirstDate || '',
    ptiSecondDate: form.ptiSecondDate || '',
    interviewerName: form.interviewerName || '',
    interviewerRole: form.interviewerRole || '',
    judgmentType: form.judgmentType || '',
    officerOpinionName: form.officerOpinionName || '',
    appealOrRevision: form.appealOrRevision || '',
    remarks: form.remarks || '',

    // Status & Metadata
    status: (item.status || form.status || 'DRAFT') as SubmissionStatus,
    submitDate: item.submitDate || form.submitDate || '',
    submittedAt: item.submittedAt || form.submittedAt || '',

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // System Dates & Audit
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory) ? item.stageHistory : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Filter Function for PLO Form Four
// ---------------------------------------------------------------------------
const ploFilterPredicate = (
  app: PLOFormFourData,
  params: FetchPLOFourParams
): boolean => {
  const { searchTerm, partySearch, selectedStatus, ptiFirstDate, ptiSecondDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  
  // PTI Date match checks
  const matchesPtiFirstDate =
    !ptiFirstDate ||
    Boolean(app.ptiFirstDate && app.ptiFirstDate.startsWith(ptiFirstDate));

  const matchesPtiSecondDate =
    !ptiSecondDate ||
    Boolean(app.ptiSecondDate && app.ptiSecondDate.startsWith(ptiSecondDate));

  // Party search filtering by victim or witness name
  const partyTerm = (partySearch || "").toLowerCase().trim();
  const matchesParty =
    !partyTerm ||
    Boolean(app.victimOrWitnessName && app.victimOrWitnessName.toLowerCase().includes(partyTerm));

  // General search filtering
  const term = (searchTerm || "").toLowerCase().trim();
  const matchesGeneralSearch =
    !term ||
    Boolean(app.victimOrWitnessName && app.victimOrWitnessName.toLowerCase().includes(term)) ||
    Boolean(app.interviewerName && app.interviewerName.toLowerCase().includes(term)) ||
    Boolean(app.interviewerRole && app.interviewerRole.toLowerCase().includes(term)) ||
    Boolean(app.officerOpinionName && app.officerOpinionName.toLowerCase().includes(term)) ||
    Boolean(app.remarks && app.remarks.toLowerCase().includes(term)) ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term));

  return Boolean(
    matchesStatus &&
    matchesPtiFirstDate &&
    matchesPtiSecondDate &&
    matchesParty &&
    matchesGeneralSearch
  );
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export interface FetchPLOFourParams {
  searchTerm?: string;
  partySearch?: string;
  selectedStatus?: string;
  ptiFirstDate?: string;
  ptiSecondDate?: string;
  currentPage: number;
  itemsPerPage: number;
}

// Fetch All / Filtered Applications
export const fetchPloApplications = (
  params: FetchPLOFourParams
): Promise<PLOFetchResponse> =>
  genericService.fetchApplications<PLOFormFourData>(
    ENDPOINT,
    params,
    mapPloFormFourBackendToFrontend,
    ploFilterPredicate
  );

// Fetch Single Application by ID
export const fetchPloById = (id: string): Promise<PLOFormFourData> =>
  genericService.fetchApplicationById<PLOFormFourData>(
    ENDPOINT,
    id,
    mapPloFormFourBackendToFrontend
  );

// Save Draft (Single item or Array/Bulk payload)
export const savePloDraft = (
  id: string | null,
  data: Partial<PLOFormFourData> | Partial<PLOFormFourData>[]
) => genericService.saveDraft<PLOFormFourData>(ENDPOINT, id, data as any);

// Delete Form (Soft Delete)
export const deletePloForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);